import { useEffect, useState } from "react";
import { vocabularize } from "src/Common/vocab";

import { formatDate, getAvatarUrl } from "src/Common/common";
import { handleError } from "src/Common/function";
import WOFormService from "src/Services/API/woform.service";
import FormViewerModal from "src/Pages/WO/Components/forms/FormViewerModal";
import { FORM_TYPES } from "src/Pages/forms/templates/components/FormTypeDropdown";
import { useNavigate } from "react-router-dom";

const FREQUENCIES = [
  { value: 0, label: "One Time" },
  { value: 1, label: "Weekly" },
  { value: 2, label: "Monthly" },
  { value: 3, label: "Quarterly" },
  { value: 4, label: "Semi-Annual" },
  { value: 5, label: "Annual" },
];

const STATUS_MAP = {
  0: { label: "Not Assigned", bg: "bg-[#D5D5D5]", text: "text-[#555]" },
  1: { label: "Assigned", bg: "bg-[#F5C97E]", text: "text-[#7A5A1E]" },
  2: { label: "Partially Filled", bg: "bg-[#D6BCFA]", text: "text-[#5B2C83]" },
  3: { label: "Submitted", bg: "bg-[#A8D5A2]", text: "text-[#2D6A2E]" },
};

const getFormTypeLabel = (value) => FORM_TYPES.find((t) => t.value === value)?.label || "—";
const getFrequencyLabel = (value) => FREQUENCIES.find((f) => f.value === Number(value))?.label || "—";
const getStatus = (value) => STATUS_MAP[value] || { label: "—", bg: "bg-gray-200", text: "text-gray-600" };

/**
 * Mockup-styled, read-only Forms table for the location detail page.
 * Mirrors the AllForms column set (minus row actions) in the new card style.
 * Reports the loaded count to the parent so the section header badge can show it.
 */
function CustomerFormsMockup({ locationId, customerId, onCountChange }) {
  const navigate = useNavigate();
  const effectiveCustomerId = locationId ?? customerId;
  const [forms, setForms] = useState([]);
  const [formsToPreview, setFormsToPreview] = useState([]);
  const [viewerShown, setViewerShown] = useState(false);

  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCustomerId]);

  useEffect(() => {
    if (onCountChange) onCountChange(forms.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms.length]);

  const fetchForms = () => {
    WOFormService.getAllFormsForLocation(effectiveCustomerId)
      .then((response) => {
        if (response.status === 200) setForms(response.data || []);
      })
      .catch((error) => handleError(error));
  };

  const handleFormClick = (form) => {
    if (form.status === 1 || form.status === 2 || form.status === 3) {
      setFormsToPreview([form]);
      setViewerShown(true);
      return;
    }
    if (form.status !== 0) return;

    navigate("/form-builder", {
      state: {
        formId: form.id,
        formName: form.name,
        formSchema: form.schema,
        formType: form.formType,
        frequency: form.frequency,
        templateId: form.template?.id,
        from: "/all-forms",
      },
    });
  };

  return (
    <>
      <FormViewerModal
        shown={viewerShown}
        title="PREVIEW FORM"
        forms={formsToPreview}
        onUnassign={() => {
          fetchForms();
          setViewerShown(false);
          setFormsToPreview([]);
        }}
        onClose={() => {
          setViewerShown(false);
          setFormsToPreview([]);
        }}
      />

      {forms.length === 0 ? (
        <div className="px-5 py-8 text-center text-[14px] text-subTitleColor font-OpenSansRegular">
          No forms found.
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-borderColor">
                <Th>Form Name</Th>
                <Th>Type</Th>
                <Th>Frequency</Th>
                <Th>Status</Th>
                <Th>Technicians</Th>
                <Th>Branch</Th>
                <Th>Created By</Th>
                <Th>{vocabularize("WO#")}</Th>
                <Th>Date Created</Th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => {
                const status = getStatus(form.status);
                return (
                  <tr key={form.id} className="border-b border-borderColor last:border-b-0 hover:bg-backgroundColor">
                    <td className="px-5 py-4">
                      <button
                        onClick={() => handleFormClick(form)}
                        className="text-[14px] font-OpenSansSemiBold text-blueColor hover:underline text-left"
                      >
                        {form.name || "Untitled"}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                      {getFormTypeLabel(form.formType)}
                    </td>
                    <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                      {form.frequency !== null && form.frequency !== undefined ? getFrequencyLabel(form.frequency) : "—"}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-[12px] font-OpenSansSemiBold ${status.bg} ${status.text}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      {form.technicians && form.technicians.length > 0 ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          {form.technicians.map((tech) => (
                            <div
                              key={tech.id}
                              title={tech.name}
                              className="flex items-center gap-1 bg-[#F3F4F6] rounded-full pl-1 pr-2 py-[2px]"
                            >
                              {tech.avatar ? (
                                <img
                                  src={getAvatarUrl(tech.avatar)}
                                  alt={tech.name || "Technician"}
                                  className="w-[18px] h-[18px] rounded-full"
                                />
                              ) : (
                                <div className="w-[18px] h-[18px] rounded-full bg-[#D1D5DB]" />
                              )}
                              <span className="text-[12px] leading-none text-titleColor">{tech.name}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[14px] font-OpenSansRegular text-subTitleColor">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                      {form.wo?.branch?.name || "—"}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {form.createdBy?.avatar && (
                          <img
                            src={getAvatarUrl(form.createdBy.avatar)}
                            alt={form.createdBy?.name || "Created by"}
                            className="w-[18px] h-[18px] rounded-full"
                          />
                        )}
                        <span className="text-[14px] font-OpenSansRegular text-subTitleColor">
                          {form.createdBy?.name || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                      {form.wo?.number ? `#${form.wo.number}` : "—"}
                    </td>
                    <td className="px-5 py-4 text-[14px] font-OpenSansRegular text-subTitleColor">
                      {form.createdAt ? formatDate(form.createdAt, "MMM D, YYYY") : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Th({ children }) {
  return (
    <th className="px-5 py-3 text-left text-[12px] font-OpenSansSemiBold text-subTitleColor whitespace-nowrap">
      {children}
    </th>
  );
}

export default CustomerFormsMockup;
